export default class {
    constructor (name, config = {}) {
        if (name === undefined || typeof name !== 'string') {
            wealthify.error.throwError('Prop class', `name passed as ${name} but must be of type string`);
        }

        if (this.constructor.reservedNames.some((reservedName) => name === reservedName)) {
            wealthify.error.throwError('Prop class', `name passed as ${name} conflicts with a reserved name and must be renamed`);
        }

        if (config.required !== undefined && !wealthify.type.boolean.isBoolean(config.required)) {
            wealthify.error.throwError(`Prop class ${name}`, `value passed to required (${config.required}) is not a boolean`);
        }

        if (config.nullable !== undefined && !wealthify.type.boolean.isBoolean(config.nullable)) {
            wealthify.error.throwError(`Prop class ${name}`, `value passed to nullable (${config.required}) is not a boolean`);
        }

        if (config?.onChangeCallback !== undefined && !wealthify.type.function.isFunction(config.onChangeCallback)) {
            wealthify.error.throwError(`Prop class ${name}`, `value passed to onChangeCallback (${config.onChangeCallback}) is not a functon`);
        }

        if (config?.formatter !== undefined && !wealthify.type.function.isFunction(config.formatter)) {
            wealthify.error.throwError(`Prop class ${name}`, `value passed to formatter (${config.formatter}) is not a functon`);
        }

        if (config?.type !== undefined && !(config.type instanceof wealthify.PropType)) {
            wealthify.error.throwError(`Prop class ${name}`, `value passed to type (${config.type}) is not an instance of PropType class`);
        }

        if (config?.validators !== undefined && !wealthify.type.array.isArray(config.validators)) {
            wealthify.error.throwError(`Prop class ${name}`, `validators passed as ${config.validators} must be of type array`);
        }

        if (config?.validators !== undefined) {
            config.validators.forEach((validator) => {
                if (!(validator instanceof wealthify.PropValidator)) {
                    wealthify.error.throwError(`Prop class ${name}`, `value passed to propValidator (${validator}) is not an instance of PropValidator class`);
                }
            });
        }

        this._name = name;
        
        this._default = config?.default;
        this._required =  config?.required ? config.required : false;
        this._nullable =  config?.nullable ? config.nullable : false;
        this._onChangeCallback = config?.onChangeCallback;
        this._formatter = config.formatter ? config.formatter : config?.type?.formatter;
        this._type = config?.type;
        this._validators = config?.validators ? config.validators : [];
        this._valid = true;
    }

    static get reservedNames () {
        return [ 'disabled', 'id', 'title' ];
    }

    get default () {
        return this._default;
    }

    get _hasFormatter () {
        return this._formatter !== undefined;
    }

    get name () {
        return this._name;
    }

    get names () {
        // Get all name aliases, null if only one
        const nameAlias = wealthify.convert.casing(this._name);
        const hasNameAlias = nameAlias !== this.name;

        return hasNameAlias ? [this._name, nameAlias] : null;
    }

    get isComplex () {
        if (!this._type) {
            return false;
        }

        return this._type.isComplex;
    }

    get isValueOverridden () {
        const isDefault = this.default && this.value === this.default;
        const isUndefined = this.value === undefined || this.value === '';

        return !isDefault && !isUndefined;
    }

    get onChangeCallback () {
        return this._onChangeCallback;
    }

    get value () {
        return this._value;
    }

    /**
     * @param {string} value
     */
    set componentName (value) {
        this._componentName = value;
    }
    
    /**
     * @param {function} callback
     */
    set onChangeCallback (onChangeCallback) {
        this._onChangeCallback = onChangeCallback;
    }

    /**
     * @param {string} value
     */
    set value (value) {
        if (this._hasFormatter && value !== undefined && value !== '') {
            value = this._formatter(value);
        }

        this._value = value;
    }

    _validate (value) {
        const validators = this._validators;
        const nullableAndNull = this._nullable && value === null;
        const requiredAndValueUndefined = this._required && value === undefined;
        const hasPropTypeAndValueDefined = this._type !== undefined && value !== undefined;
        const notRequiredAndUndefined = !this._required && value === undefined;

        if (nullableAndNull) {
            return this._valid = true;
        }

        if (requiredAndValueUndefined) {
            this._valid = false;
            
            return wealthify.error.throwError(this._componentName, `${this.name} is a required prop but its value is undefined.`);
        }

        if (hasPropTypeAndValueDefined) {
            validators.push(this._type.validator);
        }

        if (notRequiredAndUndefined || validators.length === 0) {
            return this._valid = true;
        }

        let failingValidator;

        this._valid = this._validators.every((validator) => {
            const valid = validator.validate(value);

            if (!valid) {
                failingValidator = validator.name;
            }

            return valid;
        });

        if (!this._valid) {
            wealthify.error.throwError(this._componentName, `${this.name} is failing at validator ${failingValidator} with value ${JSON.stringify(value)}`);
        }
    }

    runCallback (component) {
        const valueDefined = this._value !== undefined;

        this._validate(this._value);

        if (this._valid && valueDefined) {
            this._onChangeCallback(this._value, component);
        }
    }
}